import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import { defaults } from '../theme/base/defaults';
import { media } from '../theme/base/mixins';
import {
  StyledDivComponent,
  StyledLIComponent,
  StyledUListComponent
} from '../types';
import { ButtonLink } from './Button';
import { Icon } from './Icon';
import { Image } from './Image';
import { Section } from './Section';
import { rgba } from './utils/rgba';

const { breakpoints, paddings } = defaults;

/**
 * props:
 * - left / right -> for positioning the controls
 * - activeItem -> for showing active state on indicators
 */

export const CarouselContentWrapper: StyledDivComponent = styled.div`
  ${media.large`
    bottom: 28px;
    position: absolute;
    right: 0;
    left: 0;
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
    text-align: left;
    z-index: 10;
    padding-top: 0;
  `}

  ${Section} {
    padding-bottom: 0;
  }

  @media (min-width: ${breakpoints.xlarge + 1 + paddings.busselection * 2}px) {
    bottom: 180px;
  }

  @media (min-width: ${breakpoints.large}px) and (max-width: ${breakpoints.xlarge +
      paddings.carousel * 2}px) {
    ${Section} {
      padding-left: 80px;
      padding-right: 80px;
    }
  }
`;

export const CarouselContent: StyledDivComponent = styled.div`
  white-space: normal;
  background-color: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
  color: ${(props: ThemeProps<Theme>) => props.theme.colors.black};
  padding: ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap}px 0;

  ${media.large`
    padding: ${(props: ThemeProps<Theme>) => props.theme.grid.columnGap}px;
    background-color: ${(props: ThemeProps<Theme>) =>
      rgba(props.theme.colors.white, 0.7)};

    &:before {
      display: none;
    }
  `}

  p {
    margin-bottom: 35px;
    font-size: 1.8rem;
    line-height: 1.25;

    ${media.large`
      font-size: 2.2rem;
    `};
  }

  ${ButtonLink} {
    border-color: ${(props: ThemeProps<Theme>) => props.theme.colors.black};
    color: ${(props: ThemeProps<Theme>) => props.theme.colors.black};

    &:hover {
      background-color: ${(props: ThemeProps<Theme>) =>
        props.theme.colors.black};
      color: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
    }
  }
`;

interface CarouselProps {
  isRoot?: boolean;
}

export const Carousel: StyledDivComponent<CarouselProps> = styled.div`
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  position: relative;
  line-height: 0;
  max-height: 790px;

  ${CarouselContentWrapper} {
    ${(props: CarouselProps) =>
      props.isRoot &&
      css`
        margin-top: 300px;

        ${media.large`
          margin-top: 0;
        `};
      `}
  }
`;

export const CarouselImage: StyledDivComponent<CarouselProps> = styled.div`
  position:relative;
  width: 100%;
  height: 100%;
  overflow: hidden;

  ${(props: CarouselProps) =>
    props.isRoot &&
    css`
      height: 300px;
      position: absolute;
    `}

  ${(props: CarouselProps) =>
    !props.isRoot &&
    css`
      height: auto;
    `}

  ${media.large`
    ${(props: CarouselProps) =>
      props.isRoot &&
      css`
        height: auto;
        position: relative;
        max-height: 790px;
      `}
  `}

  ${Image} {
    width: 100%;
    position: relative;
    height: auto;

    ${(props: CarouselProps) =>
      props.isRoot &&
      css`
        width: auto;
        min-width: 100%;
        transform: translate(-50%, 0);
        left: 50%;
        position: absolute;
        height: 100%;

        ${media.large`
          width: 100%;
          position: relative;
          height: auto;
          transform: none;
          left: inherit;
        `}
      `}
  }
`;

interface CarouselItemProps {
  staticHeight?: boolean;
}

export const CarouselItem: StyledDivComponent<CarouselItemProps> = styled.div`
  width: 100%;
  position: relative;

  ${media.large`
    height: ${(props: CarouselItemProps) =>
      props.staticHeight ? '790px' : 'auto'};

    ${Image} {
      ${(props: CarouselItemProps) =>
        props.staticHeight &&
        css`
          height: 100%;
          width: auto;
          transform: translate(-50%, 0);
          left: 50%;
          position: absolute;
        `}
      }
  `}

  ${media.xxlarge`
    ${Image} {
      height: auto;
      width: 100%;

      ${(props: CarouselItemProps) =>
        props.staticHeight
          ? css`
              transform: translate(0, -50%);
              left: 0;
              top: 50%;
            `
          : null}
    }
  `};
`;

interface CarouselControlProps {
  left?: boolean;
  right?: boolean;
}

export const CarouselControl = styled.button<CarouselControlProps>`
  position: absolute;
  outline: none !important;
  top: 150px;
  transform: translateY(-50%);
  ${(props) => (props.left ? 'left: 20px' : '')};
  ${(props) => (props.right ? 'right: 20px' : '')};
  border: none;
  background: transparent;
  cursor: pointer;
  z-index: 15;

  ${media.large`
    top: 50%;
  `}

  ${Icon} {
    height: 40px;
    width: 40px;

    transform: rotate(${({ left }) => (left ? '' : '-')}90deg);
    margin-right: 0;

    svg {
      height: 40px;
      width: 40px;

      path {
        fill: ${(props: ThemeProps<Theme>) => props.theme.colors.white};
      }
    }
  }

  &:hover {
    ${Icon} {
      svg {
        path {
          fill: ${(props: ThemeProps<Theme>) => props.theme.colors.primary};
        }
      }
    }
  }
`;

interface CarouselIndicatorItemProps {
  activeItem?: boolean;
}

type CarouselIndicatorItemPropsThemed = ThemeProps<Theme> &
  CarouselIndicatorItemProps;

export const CarouselIndicatorItem: StyledLIComponent<
  CarouselIndicatorItemProps
> = styled.li`
  display: inline-block;
  width: 15px;
  height: 3px;
  margin: 5px;
  background-color: ${(props: CarouselIndicatorItemPropsThemed) =>
    props.activeItem ? props.theme.colors.secondary : props.theme.colors.gray};
  cursor: pointer;
  transition: background-color 0.5s ease-in-out 1s;
`;

export const CarouselIndicators: StyledUListComponent = styled.ul`
  display: block;
  overflow: hidden;
  position: absolute;
  right: 20px;
  /* transform: translateX(-50%); */
  z-index: 15;

  @media (min-width: ${breakpoints.large}px) and (max-width: ${breakpoints.xlarge +
      paddings.carousel * 2}px) {
    padding: 0 10px;
  }
`;

export const CarouselIndicatorWrapper: StyledDivComponent = styled.div`
  position: absolute;
  width: 100%;
  top: 280px;

  ${Section} {
    padding-bottom: 0;
  }

  ${media.large`
    top: inherit;
    bottom: 166px;
  `}

  @media (min-width: ${
    breakpoints.large
  }px) and (max-width: ${breakpoints.xlarge + paddings.carousel * 2}px) {
    bottom: 20px;
  }
`;
